// Spool list inside the Job modal. Renders one SpoolCard per spool —
// the card now owns its header, pipeline strip, foot, and (when
// expanded) the stage details table, so this component is back to
// list-management: drag/drop, bulk selection, and routing stage
// patches back to the API + onStageChanged refetch.
import { useEffect, useMemo, useRef, useState } from "react";
import { SpoolCard } from "./SpoolCard";
import { SpoolGridTable } from "./SpoolGridTable";
import { newDraft, type SpoolDraft } from "./spoolDraft";
import { BulkPrintMenu } from "../modals/BulkPrintMenu";
import { PasteSpoolsModal } from "../modals/PasteSpoolsModal";
import { patchSpool, patchSpoolStage, useStageDefaults } from "../hooks/useProductionApi";
import {
  denseStages,
  inferWeldBayKey,
  isSpoolComplete,
  planProgressStep,
  progressionStages,
} from "../lib/stageHelpers";
import { isoToday } from "../lib/dates";
import { useActiveStages } from "./StagesProvider";
import type {
  DrawingDto,
  SpoolStageDto,
  StageKey,
  StageStatus,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";

export { spoolFromDto, spoolToInput } from "./spoolDraft";
export type { SpoolDraft } from "./spoolDraft";

interface Props {
  spools: SpoolDraft[];
  onChange(next: SpoolDraft[]): void;
  jobId?: number;
  jobNumber?: string;
  focusSpoolId?: number | null;
  // When set, auto-expand the matching spool's stage panel once.
  // Mirrors focusSpoolId's scroll-arming pattern: a ref tracks the
  // last id we expanded for, and the effect only re-fires when the
  // value transitions from null/another id.
  expandSpoolId?: number | null;
  team?: TeamMemberDto[];
  stagesBySpool?: Map<number, SpoolStageDto[]>;
  weldBayBySpool?: Map<number, StageKey | null>;
  // Numeric job-default bay (1..8). Used as the per-row fallback in
  // both the SpoolGridTable layout and the bulk-progress path so a
  // spool with no persisted weld row yet resolves to the job's bay.
  jobBay?: number | null;
  // Workshop convention: one drawing per spool. SpoolCard uses this
  // to render a per-spool button that opens the linked PDF directly.
  drawingBySpoolId?: Map<number, DrawingDto>;
  onStageChanged?: () => void;
  // Cross-nav: switch modal to Drawings tab. Per-spool button on
  // SpoolCard lets workshop jump from a spool to its drawings without
  // hunting through the tab strip.
  onOpenDrawings?: () => void;
  // "cards" (default) renders one SpoolCard per spool; "table"
  // renders the compact grid. Selection + bulk-print bar are shared.
  layout?: "cards" | "table";
}

export function SpoolTable({
  spools,
  onChange,
  jobId,
  jobNumber,
  focusSpoolId,
  expandSpoolId,
  team,
  stagesBySpool,
  weldBayBySpool,
  jobBay,
  drawingBySpoolId,
  onStageChanged,
  layout = "cards",
  onOpenDrawings,
}: Props) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Date typed into the bulk bar's due-date box. Empty means the
  // apply button clears the selected spools' due dates instead.
  const [bulkDue, setBulkDue] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const flashedFor = useRef<number | null>(null);
  const expandedFor = useRef<number | null>(null);
  // Per-stage default assignees from /api/.../stage-defaults so
  // synthetic stage rows in SpoolCard pre-fill the Assigned to
  // column without each spool needing manual edits.
  const stageDefaultsQuery = useStageDefaults();
  const stageDefaultsByKey = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const d of stageDefaultsQuery.data?.defaults ?? []) {
      m.set(d.stageKey, d.defaultAssigneeId);
    }
    return m;
  }, [stageDefaultsQuery.data]);

  useEffect(() => {
    if (!focusSpoolId) return;
    if (flashedFor.current === focusSpoolId) return;
    const card = cardRefs.current.get(focusSpoolId);
    if (!card) return;
    flashedFor.current = focusSpoolId;
    card.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusSpoolId, spools]);

  useEffect(() => {
    if (focusSpoolId !== flashedFor.current) flashedFor.current = null;
  }, [focusSpoolId]);

  // Auto-expand the matching spool's stage panel when expandSpoolId
  // flips to a new value. Same one-shot guard as flashedFor: the
  // user can still collapse manually after this fires.
  useEffect(() => {
    if (!expandSpoolId) return;
    if (expandedFor.current === expandSpoolId) return;
    const target = spools.find((s) => s.id === expandSpoolId);
    if (!target) return;
    expandedFor.current = expandSpoolId;
    setExpanded((cur) => {
      if (cur.has(target.localKey)) return cur;
      const next = new Set(cur);
      next.add(target.localKey);
      return next;
    });
  }, [expandSpoolId, spools]);

  useEffect(() => {
    if (expandSpoolId !== expandedFor.current) expandedFor.current = null;
  }, [expandSpoolId]);

  const totalHours = spools.reduce((acc, s) => acc + s.hours, 0);
  // Complete = every progression stage on the spool is complete.
  // The draft's denormalised .status field isn't bumped by the
  // SpoolCard's per-stage clicks, so the previous filter stayed at
  // 0 forever; deriving from the stage rollup keeps the count in
  // sync with what each card actually displays.
  const catalogue = useActiveStages();
  const completeCount = useMemo(
    () => spools.filter((s) => isSpoolComplete({
      spool: { stages: typeof s.id === "number" ? (stagesBySpool?.get(s.id) ?? []) : [] },
      catalogue,
      jobBay: null,
    })).length,
    [spools, stagesBySpool, catalogue],
  );

  const update = (i: number, patch: Partial<SpoolDraft>) => {
    onChange(spools.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };
  const remove = (i: number) => {
    onChange(spools.filter((_, idx) => idx !== i));
  };
  const duplicate = (i: number) => {
    const src = spools[i];
    if (!src) return;
    const copy: SpoolDraft = {
      ...src,
      id: undefined,
      localKey: `dup-${Math.random().toString(36).slice(2, 10)}`,
      num: nextDupNum(src.num, spools.map((s) => s.num)),
      sortOrder: i + 1,
    };
    const next = spools.slice();
    next.splice(i + 1, 0, copy);
    onChange(next.map((s, idx) => ({ ...s, sortOrder: idx })));
  };
  const add = () => {
    onChange([...spools, newDraft(spools.length)]);
  };
  const [pasteOpen, setPasteOpen] = useState(false);
  const pasteCommit = (rows: SpoolDraft[]) => {
    // Append after the current list and renumber sortOrder so the
    // pasted rows land at the bottom in the order they were typed.
    const merged = [...spools, ...rows];
    onChange(merged.map((s, idx) => ({ ...s, sortOrder: idx })));
    setPasteOpen(false);
  };
  const drop = (toIdx: number) => {
    if (dragFrom === null || dragFrom === toIdx) {
      setDragFrom(null);
      setDragOver(null);
      return;
    }
    const next = spools.slice();
    const [moved] = next.splice(dragFrom, 1);
    if (moved) next.splice(toIdx, 0, moved);
    onChange(next.map((s, idx) => ({ ...s, sortOrder: idx })));
    setDragFrom(null);
    setDragOver(null);
  };

  const printableIds = useMemo(
    () => spools.map((s) => s.id).filter((id): id is number => typeof id === "number"),
    [spools],
  );
  const selectedIds = useMemo(
    () => printableIds.filter((id) => selected.has(id)),
    [printableIds, selected],
  );
  const allSelected = printableIds.length > 0 && selectedIds.length === printableIds.length;
  const canBulkPrint = typeof jobId === "number" && typeof jobNumber === "string";

  const toggleOne = (id: number, checked: boolean) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(printableIds) : new Set());
  };
  const toggleExpand = (key: string) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Stage patch dispatcher — passed to each SpoolCard. Skips silently
  // when the job/spool isn't persisted yet (draft mode).
  const patchStage = async (
    spoolId: number | undefined,
    stageKey: StageKey,
    patch: {
      hours?: number;
      assigneeId?: number | null;
      startDate?: string | null;
      doneDate?: string | null;
      status?: StageStatus;
    },
  ) => {
    if (typeof jobId !== "number" || typeof spoolId !== "number") return;
    await patchSpoolStage(jobId, spoolId, stageKey, patch);
    onStageChanged?.();
  };

  // Bulk due date. Persists per spool immediately (one PATCH each,
  // like the bulk progress action) and mirrors the value into the
  // drafts so the rows redraw straight away. Immediate persistence
  // matters here: any stage edit in this modal triggers a job refetch,
  // and the refetch rebuilds the spool drafts from the server — a
  // draft-only bulk edit would silently vanish. No refetch is fired
  // afterwards, so a neighbouring unsaved edit isn't clobbered by
  // this one.
  const [bulkDueBusy, setBulkDueBusy] = useState(false);
  const applyBulkDue = async () => {
    if (typeof jobId !== "number" || selectedIds.length === 0 || bulkDueBusy) return;
    const value = bulkDue || null;
    const n = selectedIds.length;
    if (value === null && !window.confirm(
      `Clear the due date on ${n} selected spool${n === 1 ? "" : "s"}?`,
    )) return;
    setBulkDueBusy(true);
    const done: number[] = [];
    try {
      for (const id of selectedIds) {
        await patchSpool(jobId, id, { dueDate: value });
        done.push(id);
      }
    } catch (e) {
      window.alert(
        `Bulk due date failed after ${done.length} of ${n} spool${n === 1 ? "" : "s"}: `
        + `${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      // Reflect whatever actually persisted, so a part-way failure
      // doesn't leave the table showing dates the server rejected.
      if (done.length > 0) {
        const target = new Set(done);
        onChange(spools.map((s) => (
          typeof s.id === "number" && target.has(s.id) ? { ...s, dueDate: value } : s
        )));
      }
      setBulkDueBusy(false);
    }
  };

  // Bulk one-step progression. Each selected spool advances exactly
  // one step from wherever its own pipeline currently sits (an
  // in-progress stage completes + the next starts; a fresh spool
  // starts its first pending stage; fully-complete spools no-op).
  // Patches go direct via patchSpoolStage; the refetch fires once at
  // the end instead of per patch.
  const [bulkProgressBusy, setBulkProgressBusy] = useState(false);
  const bulkProgress = async () => {
    if (typeof jobId !== "number" || selectedIds.length === 0 || bulkProgressBusy) return;
    const n = selectedIds.length;
    if (!window.confirm(
      `Progress ${n} selected spool${n === 1 ? "" : "s"} one step forward? Each spool advances from its own current stage.`,
    )) return;
    setBulkProgressBusy(true);
    const today = isoToday();
    try {
      for (const id of selectedIds) {
        const stages = stagesBySpool?.get(id) ?? [];
        const bay = weldBayBySpool?.get(id) ?? inferWeldBayKey(stages, jobBay ?? null);
        const dense = denseStages(stages, bay, stageDefaultsByKey, catalogue);
        const active = progressionStages(dense, bay);
        for (const p of planProgressStep(active, today)) {
          await patchSpoolStage(jobId, id, p.stageKey, p.patch);
        }
      }
      onStageChanged?.();
    } catch (e) {
      window.alert(`Bulk progress failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBulkProgressBusy(false);
    }
  };

  return (
    <>
      <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 8, display: "flex", gap: 18, fontFamily: "var(--bsp-font-mono)" }}>
        <span>Spools: <strong style={{ color: "var(--bsp-dark)" }}>{spools.length}</strong></span>
        <span>Total hours: <strong style={{ color: "var(--bsp-dark)" }}>{totalHours.toFixed(1)}h</strong></span>
        <span>Complete: <strong style={{ color: "var(--bsp-green-fg)" }}>{completeCount}/{spools.length}</strong></span>
      </div>
      {canBulkPrint ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          marginBottom: 8,
          background: "var(--bsp-surface-muted)",
          border: "1px solid var(--bsp-border)",
          borderRadius: 4,
          fontSize: 12,
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, cursor: printableIds.length === 0 ? "not-allowed" : "pointer" }}>
            <input
              type="checkbox"
              checked={allSelected}
              disabled={printableIds.length === 0}
              onChange={(e) => toggleAll(e.target.checked)}
            />
            Select all
          </label>
          <span style={{ fontFamily: "var(--bsp-font-mono)", color: "var(--bsp-fg-muted)" }}>
            {selectedIds.length} selected
          </span>
          <div style={{ flex: 1 }} />
          {selectedIds.length > 0 ? (
            <>
              <label
                style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 600 }}
                title="Due date to apply to every selected spool"
              >
                Due
                <input
                  type="date"
                  value={bulkDue}
                  onChange={(e) => setBulkDue(e.target.value)}
                  aria-label="Bulk due date"
                  style={{
                    padding: "3px 5px",
                    fontSize: 11,
                    fontFamily: "var(--bsp-font-mono)",
                    border: "1px solid var(--bsp-border-strong)",
                    borderRadius: 4,
                  }}
                />
              </label>
              <button
                type="button"
                className="btn-sm"
                onClick={() => void applyBulkDue()}
                disabled={bulkDueBusy}
                title={bulkDue
                  ? `Set the due date to ${bulkDue} on ${selectedIds.length} selected spool${selectedIds.length === 1 ? "" : "s"}`
                  : `Clear the due date on ${selectedIds.length} selected spool${selectedIds.length === 1 ? "" : "s"} — pick a date above to set one instead`}
                style={{ background: "var(--bsp-blue-tint)", borderColor: "var(--bsp-blue)", color: "var(--bsp-blue)" }}
              >
                {bulkDueBusy
                  ? "Saving…"
                  : `${bulkDue ? "📅 Set due" : "📅 Clear due"} ${selectedIds.length}`}
              </button>
            </>
          ) : null}
          {selectedIds.length > 0 ? (
            <button
              type="button"
              className="btn-sm"
              onClick={() => setSelected(new Set())}
              title="Deselect every spool"
            >
              ✕ Deselect
            </button>
          ) : null}
          {selectedIds.length > 0 ? (
            <button
              type="button"
              className="btn-sm"
              onClick={() => void bulkProgress()}
              disabled={bulkProgressBusy}
              title="Advance each selected spool one step forward from its own current stage"
              style={{ background: "var(--bsp-green-fg, #2E7D32)", color: "#fff", borderColor: "var(--bsp-green-fg, #2E7D32)" }}
            >
              {bulkProgressBusy ? "Progressing…" : `▶ Progress ${selectedIds.length}`}
            </button>
          ) : null}
          {selectedIds.length > 0 ? (
            <button
              type="button"
              className="btn-sm danger"
              onClick={() => {
                const n = selectedIds.length;
                if (!window.confirm(
                  `Delete ${n} spool${n === 1 ? "" : "s"} from this job? They'll be removed from the list — click Save to persist. Linked drawings will be unlinked on Save.`,
                )) return;
                const remove = new Set(selectedIds);
                onChange(spools.filter((s) => typeof s.id !== "number" || !remove.has(s.id)));
                setSelected(new Set());
              }}
              title="Remove the selected spools from the list (Save to persist)"
            >
              Delete {selectedIds.length}
            </button>
          ) : null}
          <BulkPrintMenu
            jobId={jobId}
            jobNumber={jobNumber}
            selectedSpoolIds={selectedIds}
          />
        </div>
      ) : null}
      {layout === "table" ? (
        <SpoolGridTable
          spools={spools}
          stagesBySpool={stagesBySpool}
          weldBayBySpool={weldBayBySpool}
          team={team ?? []}
          jobId={canBulkPrint ? jobId : undefined}
          jobBay={jobBay ?? null}
          selected={selected}
          onToggleSelect={toggleOne}
          onUpdate={update}
          onStagePatch={patchStage}
        />
      ) : (
      <div>
        {spools.map((s, i) => {
          const stages = typeof s.id === "number"
            ? stagesBySpool?.get(s.id) ?? []
            : [];
          const weldBay = typeof s.id === "number"
            ? weldBayBySpool?.get(s.id) ?? null
            : null;
          const linkedDrawing = typeof s.id === "number"
            ? drawingBySpoolId?.get(s.id)
            : undefined;
          const expandKey = s.localKey;
          return (
            <SpoolCard
              key={expandKey}
              spool={s}
              index={i}
              dragOver={dragOver === i}
              focused={typeof s.id === "number" && s.id === focusSpoolId}
              cardRef={(node) => {
                if (typeof s.id !== "number") return;
                if (node) cardRefs.current.set(s.id, node);
                else cardRefs.current.delete(s.id);
              }}
              stages={stages}
              team={team ?? []}
              weldBayKey={weldBay}
              stageDefaultAssignees={stageDefaultsByKey}
              jobId={canBulkPrint ? jobId : undefined}
              jobNumber={canBulkPrint ? jobNumber : undefined}
              expanded={expanded.has(expandKey)}
              selected={typeof s.id === "number" && selected.has(s.id)}
              linkedDrawing={linkedDrawing
                ? { id: linkedDrawing.id, filename: linkedDrawing.filename }
                : null}
              onChange={(p) => update(i, p)}
              onDelete={() => remove(i)}
              onDuplicate={() => duplicate(i)}
              onToggleExpand={() => toggleExpand(expandKey)}
              onToggleSelect={(checked) => {
                if (typeof s.id === "number") toggleOne(s.id, checked);
              }}
              onDragStart={() => setDragFrom(i)}
              onDragOver={() => setDragOver(i)}
              onDragLeave={() => setDragOver((cur) => (cur === i ? null : cur))}
              onDrop={() => drop(i)}
              onStagePatch={(key, patch) => patchStage(s.id, key, patch)}
              onCompositionSaved={onStageChanged}
              onOpenDrawings={onOpenDrawings}
            />
          );
        })}
      </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8, gap: 6 }}>
        <button type="button" className="btn-sm" onClick={() => setPasteOpen(true)}>
          Paste table
        </button>
        <button type="button" className="btn-sm" onClick={add} style={{ background: "var(--bsp-blue)", color: "#fff", borderColor: "var(--bsp-blue)" }}>
          + Add Spool
        </button>
      </div>
      <PasteSpoolsModal
        open={pasteOpen}
        startSortOrder={spools.length}
        existingNums={spools.map((s) => s.num)}
        onClose={() => setPasteOpen(false)}
        onCommit={pasteCommit}
      />
    </>
  );
}

// Pick a unique-ish num for a duplicated spool. Tries "(copy)" first;
// falls back to "(copy 2)" etc. if that collides.
function nextDupNum(src: string, existing: string[]): string {
  const set = new Set(existing);
  const base = src.replace(/\s*\(copy(?:\s*\d+)?\)$/i, "").trim();
  let attempt = `${base} (copy)`;
  let n = 1;
  while (set.has(attempt)) {
    n += 1;
    attempt = `${base} (copy ${n})`;
  }
  return attempt;
}
